import { Component, effect, inject, OnInit } from '@angular/core';
import { TopicStore } from '../../../stores/topic/topic.state';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { GlobalsStore } from '../../../stores/globals.state';
import { NgClass } from '@angular/common';
import { LogoComponent } from '../../atoms/logo/logo.component';
import { DividerModule } from 'primeng/divider';
import { CardModule } from 'primeng/card';
import {
  AtlasConfiguration,
  Commune,
  LandingPageItem,
} from '../../../models/commune';
import { Button, ButtonDirective } from 'primeng/button';
import { Story } from '../../../models/story';
import { Bookmark } from '../../../models/bookmark';
import { Topic } from '../../../models/topic';
import { StoryStore } from '../../../stores/story/story.state';
import { ActiveUserStore } from '../../../stores/user/active-user.state';

@Component({
  selector: 'app-atlas-landingpage',
  standalone: true,
  imports: [
    LogoComponent,
    NgClass,
    DividerModule,
    CardModule,
    Button,
    ButtonDirective,
  ],
  templateUrl: './atlas-landingpage.component.html',
  styleUrl: './atlas-landingpage.component.scss',
})
export class AtlasLandingpageComponent {
  readonly activeUserStore = inject(ActiveUserStore);
  readonly globalStore = inject(GlobalsStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly storyStore = inject(StoryStore);
  readonly topicStore = inject(TopicStore);
  config: AtlasConfiguration =
    this.globalStore.communeConfiguration() || this.globalStore.getBaseConfig();

  /**
   * Enhances a landing page item with additional properties based on its type.
   *
   * @param {LandingPageItem} landingPageItem - The landing page item to enhance.
   * This object must have a valid `type` to determine how the enhancement is applied.
   * @return {LandingPageItem | null} A new landing page item object with enhanced
   * properties or null if the enhancement could not be applied due to an invalid type or missing entityObject.
   */
  enhancedLandingPageItem(
    landingPageItem: LandingPageItem
  ): LandingPageItem | null {
    const entityObject = this.getEntityObject(landingPageItem);

    if (!entityObject) return null;

    switch (landingPageItem.type) {
      case 'Topic':
        return {
          ...landingPageItem,
          ...{
            name: entityObject.title,
            text: entityObject.description,
            icon: `fas fa-${entityObject.icon}`,
          },
        };
      case 'Bookmark':
        return {
          ...landingPageItem,
          ...{
            name: entityObject.title,
            text: entityObject.headline,
            icon: 'fas fa-note-sticky',
          },
        };
      case 'Story':
        return {
          ...landingPageItem,
          ...{
            name: entityObject.title,
            text: entityObject.description,
            icon: 'fas fa-file-lines',
          },
        };
      default:
        return null;
    }
  }

  /**
   * Retrieves the appropriate entity object based on the type specified in the item.
   *
   * @param {LandingPageItem} item - The item containing the type and ID used to fetch the entity object.
   * @return {Bookmark|Topic|Story|null} Returns the corresponding entity object (Bookmark, Topic, or Story) or null if the type is unrecognized.
   */
  getEntityObject(item: LandingPageItem): Bookmark | Topic | Story | null {
    switch (item.type) {
      case 'Topic':
        return this.topicStore.getById(item.id);
      case 'Bookmark':
        return this.bookmarkStore.getById(item.id);
      case 'Story':
        return this.storyStore.getById(item.id);
      default:
        return null;
    }
  }

  /**
   * Handles the call-to-action (CTA) for a landing page item based on its type.
   *
   * @param {LandingPageItem} item - The landing page item to handle. Contains details such as type and identifier.
   * @return {void} Does not return any value. Executes corresponding actions based on the item type.
   */
  landingPageItemCTA(item: LandingPageItem): void {
    switch (item.type) {
      case 'Bookmark':
        this.closeLandingPage();
        this.globalStore.triggerAtlasEvent('loadBookmark', {
          bookmarkId: item.id,
        });
        break;
      case 'Topic':
        this.globalStore.triggerAtlasEvent('loadTopic', {
          topic: this.topicStore.getById(item.id),
        });

        this.closeLandingPage();

        break;
      case 'Story':
        this.closeLandingPage();
        this.globalStore.triggerAtlasEvent('loadStory', {
          storyId: item.id,
        });
        break;
    }
  }

  /**
   * Returns a label based on the provided type.
   *
   * @param {string} type The type for which the label will be returned. Valid types are 'Bookmark', 'Topic', and 'Story'.
   * @return {string} The label corresponding to the given type. Returns an empty string if the type is not recognized.
   */
  getLabelByType(type: string) {
    switch (type) {
      case 'Bookmark':
        return 'zur Ansicht';
      case 'Topic':
        return 'Thema aufrufen';
      case 'Story':
        return 'zum Bericht';
      default:
        return '';
    }
  }

  /**
   * Invokes the search functionality by triggering an Atlas event named 'openSearch'.
   *
   * @return {void} Does not return a value.
   */
  callSearch(): void {
    this.globalStore.triggerAtlasEvent('openSearch', {});
  }

  /**
   * Invokes the search functionality by triggering an Atlas event named 'openSearch'.
   *
   * @return {void} Does not return a value.
   */
  callStartPage(): void {
    this.globalStore.triggerAtlasEvent('callStartPage', {});
  }

  /**
   * Closes the landing page by triggering the 'closeLandingPage' event.
   * This method uses a delayed timeout to perform the action.
   *
   * @return {void} Does not return a value.
   */
  closeLandingPage(): void {
    setTimeout(() => {
      this.globalStore.triggerAtlasEvent('closeLandingPage', {});
    });
  }

  /**
   * Checks if a default bookmark exists for the active user's context within the active commune.
   *
   * @return {boolean} Returns `true` if a default bookmark exists, otherwise `false`.
   */
  checkDefaultBookmark(): boolean {
    const commune = this.globalStore.activeCommune();
    if (!commune) return false;

    const context = this.activeUserStore.context();
    return !!this.getBookmarkByContext(context, commune);
  }

  /**
   * Retrieves a bookmark based on the provided context and commune.
   *
   * @param {string} context - The context to determine which bookmark to retrieve, e.g., 'atlasInternal'.
   * @param {Commune} commune - The commune object containing bookmark identifiers.
   * @return {Bookmark | null} The bookmark corresponding to the given context, or null if it cannot be found.
   */
  private getBookmarkByContext(
    context: string,
    commune: Commune
  ): Bookmark | null {
    const defaultBookmarkId =
      context === 'atlasInternal'
        ? commune.defaultBookmark
        : commune.defaultBookmarkExternal;

    return this.bookmarkStore.getById(defaultBookmarkId);
  }
}
